import { c as _c } from "react/compiler-runtime";
import { ChainBadge, getCurrencyKey, useChains } from "@opensea/app-kit";
import { AnimatedNumber, classNames, FlexCenter, FlexColumn, Input, NumberDisplay, Skeleton, SpaceBetween, TextBody, TextOverflowTooltip, Tooltip, UnstyledButton } from "@opensea/ui-kit";
import { ExpandMore, SwapVert } from "@opensea/ui-kit/icons";
import { bn } from "@opensea/utils";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { usePublicClient } from "wagmi";
import { useBalances } from "../../providers/BalancesProvider";
import { getFeeBufferAmount } from "../../utils/gasBuffer";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { CurrencyQuantityPresetButton } from "./CurrencyQuantityPresetButton";
import { CurrencyQuantityUsdPresetButton } from "./CurrencyQuantityUsdPresetButton";
import { CurrencySelectModal } from "./CurrencySelectModal";
import { CurrencySelectSheet } from "./CurrencySelectSheet";
import { useCurrencyInputValues } from "./hooks/useCurrencyInputValues";
const CURRENCY_DISPLAY_CLASS_NAME = "!font-mono !text-heading-md !font-medium";
const PRESET_INPUT_MULTIPLERS = [0.25, 0.5, 0.75, 1];
const USD_PRESET_AMOUNTS = [100, 200, 500, 1000];
const NUMERIC_INPUT_REGEX = /^\d*\.?\d*$/;
const TRAILING_ZEROS_REGEX = /\.?0+$/;
export function CurrencyQuantityInput({
  currency,
  quantity,
  enableUsdInput = false,
  onQuantityChange,
  onCurrencyChange,
  isDisabled,
  direction,
  container,
  isLoading,
  isLoadingCurrency,
  error,
  hasError,
  hasWarning,
  surface = "inline",
  placeholder = "0",
  overrides,
  variant = "default",
  usdValue,
  isSwapContext = false,
  disableCurrencySelection = false,
  enableBuyAmountInput = false
}) {
  const t = useTranslations("wallet.CurrencyQuantityInput");
  const [isSelectingCurrency, setIsSelectingCurrency] = useState(false);
  const isFromAsset = direction === "from";
  const [displayValue, setDisplayValue] = useState("");
  const {
    getBalanceOfCurrency
  } = useBalances();
  const chains = useChains();
  const formContext = useFormContext();
  const isUsdInputMode = useWatch({
    control: formContext.control,
    name: "isUsdInputMode"
  });
  const isNative = currency?.contractAddress === "0x0000000000000000000000000000000000000000" || currency?.contractAddress === "11111111111111111111111111111111";
  const [localIsUsdInput, setLocalIsUsdInput] = useState(false);
  const isUsdInput = isSwapContext ? isUsdInputMode : localIsUsdInput;
  const setIsUsdInput = isSwapContext ? value => {
    if (typeof value === "function") {
      const currentValue = formContext.getValues("isUsdInputMode") ?? false;
      formContext.setValue("isUsdInputMode", value(currentValue));
    } else {
      formContext.setValue("isUsdInputMode", value);
    }
  } : setLocalIsUsdInput;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const balance = currency ? getBalanceOfCurrency(currency) : undefined;
  const isSheet = surface === "sheet";
  const selectedChain = currency?.chainIdentifier ? chains.getChain(currency.chainIdentifier) : undefined;
  const publicClient = usePublicClient({
    chainId: selectedChain?.networkId
  });
  const currencySelectProps = {
    open: isSelectingCurrency,
    onOpenChange: setIsSelectingCurrency,
    chain: undefined,
    direction,
    onSelectCurrency: currency_0 => {
      onCurrencyChange(currency_0);
      setIsSelectingCurrency(false);
    },
    removeSwapDisabledCurrencies: true
  };
  const {
    quantityValue,
    usdTotalValue
  } = useCurrencyInputValues({
    currency,
    usdValue,
    quantity,
    direction,
    isLoading
  });
  const updateDisplayValue = cryptoQuantity => {
    if (isUsdInput && currency?.usdPrice) {
      const usdAmount = cryptoQuantity.times(currency.usdPrice);
      setDisplayValue(usdAmount.toFixed(2).replace(TRAILING_ZEROS_REGEX, ""));
    } else {
      setDisplayValue(cryptoQuantity.toString());
    }
  };
  const validateAndRoundQuantity = value_0 => {
    if (!currency) {
      return value_0;
    }
    const decimalPlaces = value_0.decimalPlaces() || 0;
    if (decimalPlaces > currency.decimals) {
      return value_0.decimalPlaces(currency.decimals);
    }
    return value_0;
  };
  const convertUsdToCrypto = usdAmount_0 => {
    if (!currency?.usdPrice || bn(currency.usdPrice).lte(0)) {
      return null;
    }
    return usdAmount_0.div(currency.usdPrice);
  };
  const handleQuantityUpdate = value_1 => {
    if (!value_1) {
      onQuantityChange?.(undefined);
      setDisplayValue("");
      return;
    }
    const validated = validateAndRoundQuantity(value_1);
    onQuantityChange?.(validated.toNumber());
    updateDisplayValue(validated);
  };
  useEffect(() => {
    if (!(isFromAsset && currency)) {
      return;
    }

    // Preserve user input while typing; don't override display formatting
    if (isInputFocused) {
      return;
    }
    if (quantity === undefined) {
      setDisplayValue("");
      return;
    }
    if (isUsdInput && currency.usdPrice) {
      const usdAmount_1 = bn(quantity).times(currency.usdPrice);
      const formatted = usdAmount_1.toFixed(2);
      setDisplayValue(formatted.replace(TRAILING_ZEROS_REGEX, ""));
    } else {
      setDisplayValue(quantity.toString());
    }
  }, [isFromAsset, quantity, currency, currency?.usdPrice, isUsdInput, isInputFocused]);
  return <>
      <FlexColumn className={classNames("group/input h-40 rounded-xl", isLoading && "animate-pulse")}>
        <FlexColumn className="h-full justify-between gap-6 p-4">
          <SpaceBetween>
            {enableBuyAmountInput ? null : <TextBody color="text-secondary" size="sm">
                {isFromAsset ? t("sell") : t("buy")}
              </TextBody>}
            {isFromAsset && <FlexCenter className="gap-2">
                {PRESET_INPUT_MULTIPLERS.map(preset => <CurrencyQuantityPresetButton balance={balance} balanceMultiplier={preset} gasLimit={400_000} getFeeBufferAmount={getFeeBufferAmount} key={preset} onClick={presetQuantity => {
              if (presetQuantity === undefined) {
                onQuantityChange?.(undefined);
                return;
              }
              if (currency) {
                handleQuantityUpdate(bn(presetQuantity));
              } else {
                onQuantityChange?.(presetQuantity);
              }
            }} publicClient={publicClient} quantity={quantity} />)}
              </FlexCenter>}
            {enableBuyAmountInput && <FlexCenter className="gap-2">
                {USD_PRESET_AMOUNTS.map(preset_0 => <CurrencyQuantityUsdPresetButton currency={currency} key={preset_0} onClick={presetQuantity_0 => {
              if (presetQuantity_0 === undefined || presetQuantity_0 === 0) {
                onQuantityChange?.(undefined);
                return;
              }
              if (currency) {
                handleQuantityUpdate(bn(presetQuantity_0));
              } else {
                onQuantityChange?.(presetQuantity_0);
              }
            }} quantity={quantity} usdAmount={preset_0} />)}
              </FlexCenter>}
          </SpaceBetween>
          <SpaceBetween className="h-7 items-center gap-[13px]">
            {isFromAsset || enableBuyAmountInput ? <Input className={classNames("leading-7", hasError && "text-error-1", hasWarning && !hasError && "text-warning")} disabled={isDisabled} onBlur={() => setIsInputFocused(false)} onChange={e => {
            const inputValue = e.target.value;
            setDisplayValue(inputValue);
            if (!onQuantityChange) {
              return;
            }
            if (!inputValue) {
              handleQuantityUpdate(undefined);
              return;
            }
            if (!NUMERIC_INPUT_REGEX.test(inputValue)) {
              return;
            }
            try {
              const inputBn = bn(inputValue);
              if (inputBn.lt(0)) {
                return;
              }
              if (isUsdInput && currency?.usdPrice) {
                const cryptoQuantity_0 = convertUsdToCrypto(inputBn);
                if (cryptoQuantity_0) {
                  onQuantityChange?.(validateAndRoundQuantity(cryptoQuantity_0).toNumber());
                }
              } else {
                const validated_0 = currency ? validateAndRoundQuantity(inputBn) : inputBn;
                onQuantityChange?.(validated_0.toNumber());
              }
            } catch (error_0) {
              console.error("Invalid number input:", error_0);
            }
          }} onFocus={() => setIsInputFocused(true)} onWheel={e_0 => e_0.preventDefault()} overrides={{
            Input: {
              className: CURRENCY_DISPLAY_CLASS_NAME
            },
            Container: {
              className: "h-7"
            }
          }} placeholder={placeholder} size="md" startEnhancer={<TextBody className={CURRENCY_DISPLAY_CLASS_NAME}>
                    {isUsdInput ? "$" : undefined}
                  </TextBody>} step={0.000_000_000_000_000_001} type="number" value={displayValue} variant="ghost" {...overrides?.Input} /> : <TextOverflowTooltip className="pr-3">
                <TextBody className={classNames(CURRENCY_DISPLAY_CLASS_NAME, "transition-opacity duration-1000", quantity === 0 && "opacity-75", hasError && "text-error-1", hasWarning && !hasError && "text-warning")}>
                  <AnimatedNumber display="full" value={quantityValue} />
                </TextBody>
              </TextOverflowTooltip>}

            <motion.div layout="position">
              <Tooltip content={t("nativeCurrencyOnlyTooltip")} disabled={!(disableCurrencySelection && isNative)}>
                <span>
                  <Button className="w-max px-2.5" disabled={isLoadingCurrency || disableCurrencySelection} onClick={() => setIsSelectingCurrency(true)} variant={variant === "frosted" ? "frosted" : "secondary"}>
                    <FlexCenter className="gap-1">
                      {isLoadingCurrency ? <Skeleton className="w-[70px]" /> : currency ? <CurrencyLockup currency={currency} /> : <TextBody size="sm" weight="semibold">
                          {t("selectToken")}
                        </TextBody>}
                      {!disableCurrencySelection && <ExpandMore size={20} />}
                    </FlexCenter>
                  </Button>
                </span>
              </Tooltip>
            </motion.div>
          </SpaceBetween>

          <SpaceBetween className="h-6 items-center">
            <FlexCenter>
              {error ? <TextBody color="error-1" size="sm">
                  {error}
                </TextBody> : <FlexCenter className="gap-1">
                  <TextBody className={classNames("font-mono", "transition-opacity duration-1000")} color="text-secondary" size="sm">
                    {isUsdInput && (isFromAsset || enableBuyAmountInput) ? <>
                        <AnimatedNumber display="standard" value={quantityValue} />
                        &nbsp;{currency?.symbol}
                      </> : <AnimatedNumber display="usd" value={usdTotalValue} />}
                  </TextBody>
                  {(isFromAsset || enableBuyAmountInput) && currency && enableUsdInput && <UnstyledButton onClick={() => setIsUsdInput(v => !v)}>
                        <SwapVert fill="text-secondary" size={20} />
                      </UnstyledButton>}
                </FlexCenter>}
            </FlexCenter>
            <motion.div layout="position">
              <TextBody className="font-mono" color="text-secondary" size="sm">
                {isLoadingCurrency ? <Skeleton className="w-[70px]" /> : <>
                    <NumberDisplay value={balance?.quantity ?? 0} />
                    &nbsp;
                    {currency?.symbol}
                  </>}
              </TextBody>
            </motion.div>
          </SpaceBetween>
        </FlexColumn>
      </FlexColumn>
      {isSheet ? <CurrencySelectSheet container={container} {...currencySelectProps} /> : <CurrencySelectModal {...currencySelectProps} />}
    </>;
}
function CurrencyLockup(t0) {
  const $ = _c(13);
  const {
    currency
  } = t0;
  let t1;
  if ($[0] !== currency.chain) {
    t1 = <ChainBadge chain={currency.chain} size="xs" />;
    $[0] = currency.chain;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== currency) {
    t2 = getCurrencyKey(currency);
    $[2] = currency;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currency.imageUrl || $[5] !== t1 || $[6] !== t2) {
    t3 = <Avatar aria-hidden="true" badge={t1} border={true} className="bg-white" rounded={true} seed={t2} size={24} src={currency.imageUrl} />;
    $[4] = currency.imageUrl;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== currency.symbol) {
    t4 = <TextBody size="sm" weight="semibold">{currency.symbol}</TextBody>;
    $[8] = currency.symbol;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <FlexCenter className="gap-2">{t3}{t4}</FlexCenter>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}